import React from "react";
import "./App.css";
import RoutesPath from "./routes/RoutesPath";
import { createGlobalStyle, ThemeProvider } from "styled-components";
import { Theme } from "styled-system";
import { transparentize } from "polished";
import { ModalsProvider, useModals } from "./context/modalsContext";
import { RootState, store } from "./store";
import { Provider as ReduxProvider, useSelector } from "react-redux";
import { StoreProvider } from "./context/storesContext";
import "@carrefour/design-system-styles/dist/themes/default.css";
import { AuthProvider } from "./Auth/AuthProvider";
import { SessionProvider } from "./context/sessionContext";
import { ConfigProvider } from "antd";
import fr_FR from "antd/es/locale/fr_FR";
import { TerritoryProvider } from "./context/territoriesContext";
import { RangeProvider } from "./context/rangeContext";
import "./i18n";
import I18nLanguageInitializer from "./i18n/I18nLanguageInitializer";
import { isRegionMode } from "./utils/modeUtils";

type GlobalStyleProps = {
  isModalOpen: boolean;
  isRegionMode: boolean;
};

const BaseGlobalStyle = createGlobalStyle<GlobalStyleProps>`
  body {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    overflow: ${(props) => (props.isModalOpen || props.isRegionMode ? "hidden" : "auto")};
  }
`;
const GlobalStyle = () => {
  const { isModalOpen } = useModals();
  const mode = useSelector((state: RootState) => state?.mode.value);
  const isRegion  = isRegionMode(mode);
  return <BaseGlobalStyle isModalOpen={isModalOpen} isRegionMode={isRegion} />;
};

const theme: Theme = {
  breakpoints: ["1024px"],
  fonts: ["system-ui", "Open Sans"],
  fontSizes: [10, 12, 14, 16, 20],
  fontWeights: { regular: 400, bold: 700 },
  colors: {
    brand1: "#095BA5",
    brand2: "#E81E26",
    brand3: "#575757",
    brand4: "#0E3368",
    brand4Light: `${transparentize(0.6, "#0E3368")}`,
    brand5: "#F0F3F6",
    black: "#000000",
    blackSmooth: "#121212",
    grey6: "#7C7C7C",
    grey5: "#575757",
    grey4: "#929292",
    grey3: "#D2D2D2",
    grey2: "#E5E5E5",
    grey1: "#F7F7F7",
    greyBase: "#F0F3F6",
    white: "#FFFFFF",
    greenDark: "#33691E",
    greenMedium: "#4CCD8A",
    greenLight: "#F9FFF2",
    redDark: "#AA0D11",
    redMedium: "#E81E26",
    redLight: "#FFEFEF",
    blueDark: "#0E3368",
    blueDarkVeil: `${transparentize(0.6, "#0E3368")}`,
    blueMedium: "#0970E6",
    blueLight: "#C4EFFF",
    yellowDark: "#EFB91B",
    yellowMedium: "#F7E257",
    yellowLight: "#FFF78F",
    yellowLighter: `${transparentize(0.3, "#FFF78F")}`,
    stripedRed: `repeating-linear-gradient(135deg, #FFEFEF, #FFEFEF 8px, ${transparentize(
      0.65,
      "#AA0D11"
    )} 8px, ${transparentize(0.65, "#AA0D11")} 18px)`,
    stripedGreen: `repeating-linear-gradient(
    135deg,
    #f9fff2,
    #f9fff2 8px,
    ${transparentize(0.15, "#DCEDC8")} 8px,
    ${transparentize(0.15, "#DCEDC8")} 18px
  )`,
    stripedBlue: `repeating-linear-gradient(135deg, ${transparentize(
      0.3,
      "#F5FAFF"
    )}, ${transparentize(0.3, "#F5FAFF")} 8px, ${transparentize(
      0.65,
      "#173EB4"
    )} 8px, ${transparentize(0.65, "#173EB4")} 18px)`,
  },
  space: [0, 8, 16, 24, 32, 40, 48, 56, 64],
  sizes: [0, 8, 16, 24, 32, 40, 48, 56, 64],
  radii: [0, 8, 16, 24, 32, 40, 48, 56, 64],
};

function App() {
  return (
    <AuthProvider>
      <ReduxProvider store={store}>
        <SessionProvider>
          <ConfigProvider locale={fr_FR}>
            <StoreProvider>
              <TerritoryProvider>
                <RangeProvider>
                  <ThemeProvider theme={theme}>
                    <ModalsProvider>
                      <div className="App">
                        <GlobalStyle />
                        <I18nLanguageInitializer>
                          <RoutesPath />
                        </I18nLanguageInitializer>
                      </div>
                    </ModalsProvider>
                  </ThemeProvider>
                </RangeProvider>
              </TerritoryProvider>
            </StoreProvider>
          </ConfigProvider>
        </SessionProvider>
      </ReduxProvider>
    </AuthProvider>
  );
}

export default App;
